import { ICacheService } from './ICacheService';

type CacheRecord<T> = {
	value: T;
	createdAt: number;
	expiresAt: number;
};

export class CacheService implements ICacheService {
	private dbName: string;
	private storeName: string;
	private dbPromise?: Promise<IDBDatabase>;
	private readonly defaultTTLms: number;
	private readonly cleanupIntervalMs: number;
	private lastCleanupAt: number;
	private cleanupPromise?: Promise<void>;

	constructor(dbName: string, storeName: string = 'cache') {
		this.dbName = dbName;
		this.storeName = storeName;
		this.defaultTTLms = 30 * 60 * 1000;
		this.cleanupIntervalMs = 30 * 60 * 1000;
		this.lastCleanupAt = 0;
	}

	private openDB(): Promise<IDBDatabase> {
		if (this.dbPromise) {
			return this.dbPromise;
		}

		this.dbPromise = new Promise((resolve, reject) => {
			const request = indexedDB.open(this.dbName, 1);
			request.onupgradeneeded = () => {
				const db = request.result;
				if (!db.objectStoreNames.contains(this.storeName)) {
					db.createObjectStore(this.storeName);
				}
			};
			request.onsuccess = () => {
				const db = request.result;
				db.onversionchange = () => {
					db.close();
					this.dbPromise = undefined;
				};
				resolve(db);
			};
			request.onerror = () => {
				this.dbPromise = undefined;
				reject(request.error);
			};
		});

		return this.dbPromise;
	}

	private isCacheRecord(value: unknown): value is CacheRecord<unknown> {
		return !!value
			&& typeof value === 'object'
			&& 'expiresAt' in value
			&& 'value' in value
			&& typeof (value as { expiresAt?: unknown }).expiresAt === 'number';
	}

	private async runCleanupIfNeeded(): Promise<void> {
		const now = Date.now();
		if ((now - this.lastCleanupAt) < this.cleanupIntervalMs) {
			return;
		}

		if (!this.cleanupPromise) {
			this.cleanupPromise = this.cleanupExpiredEntries(now)
				.finally(() => {
					this.lastCleanupAt = Date.now();
					this.cleanupPromise = undefined;
				});
		}

		await this.cleanupPromise;
	}

	private async cleanupExpiredEntries(referenceTime: number): Promise<void> {
		const db = await this.openDB();
		await new Promise<void>((resolve, reject) => {
			const tx = db.transaction(this.storeName, 'readwrite');
			const store = tx.objectStore(this.storeName);
			const cursorReq = store.openCursor();

			cursorReq.onsuccess = () => {
				const cursor = cursorReq.result;
				if (!cursor) {
					resolve();
					return;
				}

				const record = cursor.value;
				if (this.isCacheRecord(record) && record.expiresAt <= referenceTime) {
					cursor.delete();
				}

				cursor.continue();
			};

			cursorReq.onerror = () => reject(cursorReq.error);
			tx.onerror = () => reject(tx.error);
		});
	}

	async get<T>(key: string): Promise<T | null> {
		try {
			await this.runCleanupIfNeeded();
			const db = await this.openDB();
			return await new Promise<T | null>((resolve, reject) => {
				const tx = db.transaction(this.storeName, 'readonly');
				const store = tx.objectStore(this.storeName);
				const req = store.get(key);
				req.onsuccess = () => {
					const result = req.result;
					if (this.isCacheRecord(result)) {
						if (result.expiresAt <= Date.now()) {
							void this.delete(key);
							resolve(null);
							return;
						}
						resolve((result.value as T) ?? null);
						return;
					}

					resolve((result as T) ?? null);
				};
				req.onerror = () => reject(req.error);
			});
		} catch (error) {
			console.error('CacheService.get error:', error);
			return null;
		}
	}

	async set<T>(key: string, value: T): Promise<void> {
		try {
			await this.runCleanupIfNeeded();
			const db = await this.openDB();
			await new Promise<void>((resolve, reject) => {
				const tx = db.transaction(this.storeName, 'readwrite');
				const store = tx.objectStore(this.storeName);
				const now = Date.now();
				const record: CacheRecord<T> = {
					value,
					createdAt: now,
					expiresAt: now + this.defaultTTLms
				};
				const req = store.put(record, key);
				req.onsuccess = () => resolve();
				req.onerror = () => reject(req.error);
			});
		} catch (error) {
			console.error('CacheService.set error:', error);
		}
	}

	async delete(key: string): Promise<void> {
		try {
			const db = await this.openDB();
			await new Promise<void>((resolve, reject) => {
				const tx = db.transaction(this.storeName, 'readwrite');
				const store = tx.objectStore(this.storeName);
				const req = store.delete(key);
				req.onsuccess = () => resolve();
				req.onerror = () => reject(req.error);
			});
		} catch (error) {
			console.error('CacheService.delete error:', error);
		}
	}

	async clear(): Promise<void> {
		try {
			const db = await this.openDB();
			await new Promise<void>((resolve, reject) => {
				const tx = db.transaction(this.storeName, 'readwrite');
				const store = tx.objectStore(this.storeName);
				const req = store.clear();
				req.onsuccess = () => resolve();
				req.onerror = () => reject(req.error);
			});
		} catch (error) {
			console.error('CacheService.clear error:', error);
		}
	}
}

